// App entry point
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { FeedbackProvider } from "@/contexts/FeedbackContext";
import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { ClickTracker } from "@/components/analytics/ClickTracker";
import { ViewTracker } from "@/components/analytics/ViewTracker";
import { ScrollToTop } from "@/components/ScrollToTop";
import { lazyWithRetry } from "@/lib/dynamicImportRetry";

// Keep only the landing page eagerly loaded to minimize initial JS.
// All other routes are code-split and loaded on demand.
import Home from "./pages/Home";

// Customer-facing learn pages - lazy loaded
const LearnIndex = lazy(lazyWithRetry(() => import("./pages/LearnIndex")));
const LearnRouter = lazy(lazyWithRetry(() => import("./pages/LearnRouter")));
const LearnChapter = lazy(lazyWithRetry(() => import("./pages/LearnChapter")));
const LearnSeoDetail = lazy(lazyWithRetry(() => import("./pages/LearnSeoDetail")));

// Lazy loaded pages with retry for chunk loading errors
const BrowseCourses = lazy(lazyWithRetry(() => import("./pages/BrowseCourses")));
const LearnCourse = lazy(lazyWithRetry(() => import("./pages/LearnCourse")));
const About = lazy(lazyWithRetry(() => import("./pages/About")));
const Blog = lazy(lazyWithRetry(() => import("./pages/Blog")));
const BlogTechnical = lazy(lazyWithRetry(() => import("./pages/BlogTechnical")));
const BlogMaintainability = lazy(lazyWithRetry(() => import("./pages/BlogMaintainability")));
const BlogAnalytics = lazy(lazyWithRetry(() => import("./pages/BlogAnalytics")));
const BlogBuildingWithAI = lazy(lazyWithRetry(() => import("./pages/BlogBuildingWithAI")));
const BlogModernStore = lazy(lazyWithRetry(() => import("./pages/BlogModernStore")));
const BlogStoreGuideAINative = lazy(lazyWithRetry(() => import("./pages/BlogStoreGuideAINative")));
const BlogStoreGuideCloudflare = lazy(lazyWithRetry(() => import("./pages/BlogStoreGuideCloudflare")));
const BlogStoreGuideShopify = lazy(lazyWithRetry(() => import("./pages/BlogStoreGuideShopify")));
const BlogPost = lazy(lazyWithRetry(() => import("./pages/BlogPost")));
const Login = lazy(lazyWithRetry(() => import("./pages/Login")));
const Signup = lazy(lazyWithRetry(() => import("./pages/Signup")));
const ResetPassword = lazy(lazyWithRetry(() => import("./pages/ResetPassword")));
const Dashboard = lazy(lazyWithRetry(() => import("./pages/Dashboard")));
const CourseManager = lazy(lazyWithRetry(() => import("./pages/CourseManager")));
const QuizLearnManager = lazy(lazyWithRetry(() => import("./pages/QuizLearnManager")));
const QuizLearn = lazy(lazyWithRetry(() => import("./pages/QuizLearn")));
const AdminAnalytics = lazy(lazyWithRetry(() => import("./pages/AdminAnalyticsUnified")));
const AdminCourseAnalytics = lazy(lazyWithRetry(() => import("./pages/AdminCourseAnalytics")));
const AdminDiagnostics = lazy(lazyWithRetry(() => import("./pages/AdminDiagnostics")));
const AdminBrandKit = lazy(lazyWithRetry(() => import("./pages/AdminBrandKit")));
const AdminUsers = lazy(lazyWithRetry(() => import("./pages/AdminUsers")));
const AdminSeoPages = lazy(lazyWithRetry(() => import("./pages/AdminSeoPages")));
const AdminDocs = lazy(lazyWithRetry(() => import("./pages/AdminDocs")));
const AdminSearchAnalytics = lazy(lazyWithRetry(() => import("./pages/AdminSearchAnalytics")));
const AdminAnalyticsHub = lazy(lazyWithRetry(() => import("./pages/AdminAnalyticsHub")));
const AdminQuizAnalytics = lazy(lazyWithRetry(() => import("./pages/AdminQuizAnalytics")));
const AdminCrawlerAnalytics = lazy(lazyWithRetry(() => import("./pages/AdminCrawlerAnalytics")));
const AdminErrorLog = lazy(lazyWithRetry(() => import("./pages/AdminErrorLog")));
const AdminDocuments = lazy(lazyWithRetry(() => import("./pages/AdminDocuments")));
const AdminFeedback = lazy(lazyWithRetry(() => import("./pages/AdminFeedback")));
const AdminHomeSettings = lazy(lazyWithRetry(() => import("./pages/AdminHomeSettings")));
const AdminPerformanceScorecard = lazy(lazyWithRetry(() => import("./pages/AdminPerformanceScorecard")));
const AdminCertificates = lazy(lazyWithRetry(() => import("./pages/AdminCertificates")));
const AdminEndlessQuiz = lazy(lazyWithRetry(() => import("./pages/AdminEndlessQuiz")));
const AdminAdsSettings = lazy(lazyWithRetry(() => import("./pages/AdminAdsSettings")));
const WhatWouldYouDoPage = lazy(lazyWithRetry(() => import("./pages/WhatWouldYouDoPage")));
const Contact = lazy(lazyWithRetry(() => import("./pages/Contact")));
const AdminMailbox = lazy(lazyWithRetry(() => import("./pages/AdminMailbox")));

const Analytics = lazy(lazyWithRetry(() => import("./pages/Analytics")));
const Terms = lazy(lazyWithRetry(() => import("./pages/Terms")));
const Privacy = lazy(lazyWithRetry(() => import("./pages/Privacy")));
const Disclaimer = lazy(lazyWithRetry(() => import("./pages/Disclaimer")));
const Refunds = lazy(lazyWithRetry(() => import("./pages/Refunds")));
const CertificateView = lazy(lazyWithRetry(() => import("./pages/CertificateView")));
const SharedBadge = lazy(lazyWithRetry(() => import("./pages/SharedBadge")));
const CreatorProfile = lazy(lazyWithRetry(() => import("./pages/CreatorProfile")));
const Products = lazy(lazyWithRetry(() => import("./pages/Products")));
const ConceptLLMIntroduction = lazy(lazyWithRetry(() => import("./pages/ConceptLLMIntroduction")));
const ConceptRAG = lazy(lazyWithRetry(() => import("./pages/ConceptRAG")));
const ConceptMCP = lazy(lazyWithRetry(() => import("./pages/ConceptMCP")));
const ConceptAgents = lazy(lazyWithRetry(() => import("./pages/ConceptAgents")));
const ConceptKnowledgeGraph = lazy(lazyWithRetry(() => import("./pages/ConceptKnowledgeGraph")));
const ConceptVectorDatabases = lazy(lazyWithRetry(() => import("./pages/ConceptVectorDatabases")));
const KnowledgeTree = lazy(lazyWithRetry(() => import("./pages/KnowledgeTree")));
const NotFound = lazy(lazyWithRetry(() => import("./pages/NotFound")));

// Loading fallback for lazy routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

// Optimized QueryClient with better defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes default
      gcTime: 1000 * 60 * 30, // 30 minutes garbage collection
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Lazy load AdminBacklog
const AdminBacklog = lazy(lazyWithRetry(() => import("./pages/AdminBacklog")));

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <FeedbackProvider>
        <TooltipProvider>
          <ClickTracker />
          <Toaster />
          <Sonner />
          <HelmetProvider>
            <BrowserRouter>
              <ScrollToTop />
              <ViewTracker />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/technical" element={<BlogTechnical />} />
                  <Route path="/blog/maintainability" element={<BlogMaintainability />} />
                  <Route path="/blog/analytics" element={<BlogAnalytics />} />
                  <Route path="/blog/building-with-ai" element={<BlogBuildingWithAI />} />
                  <Route path="/blog/modern-store" element={<BlogModernStore />} />
                  <Route path="/blog/guide-ai-native-store" element={<BlogStoreGuideAINative />} />
                  <Route path="/blog/guide-cloudflare-store" element={<BlogStoreGuideCloudflare />} />
                  <Route path="/blog/guide-shopify-only-store" element={<BlogStoreGuideShopify />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/browse" element={<BrowseCourses />} />
                  <Route path="/course/:courseId" element={<LearnCourse />} />
                  <Route path="/learn" element={<LearnIndex />} />
                  <Route path="/learn/:chapterId" element={<LearnRouter />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/course-manager" element={<CourseManager />} />
                  <Route path="/quiz-learn-manager" element={<QuizLearnManager />} />
                  <Route path="/quiz-learn" element={<QuizLearn />} />
                  <Route path="/endless-quiz" element={<WhatWouldYouDoPage />} />
                  <Route path="/wwyd" element={<WhatWouldYouDoPage />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/disclaimer" element={<Disclaimer />} />
                  <Route path="/refunds" element={<Refunds />} />
                  <Route path="/admin" element={<AdminDiagnostics />} />
                  <Route path="/admin/analytics" element={<AdminAnalytics />} />
                  <Route path="/admin/course-analytics" element={<AdminCourseAnalytics />} />
                  <Route path="/admin/brand-kit" element={<AdminBrandKit />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/seo-pages" element={<AdminSeoPages />} />
                  <Route path="/admin/docs" element={<AdminDocs />} />
                  <Route path="/admin/search-analytics" element={<AdminSearchAnalytics />} />
                  <Route path="/admin/analytics-hub" element={<AdminAnalyticsHub />} />
                  <Route path="/admin/quiz-analytics" element={<AdminQuizAnalytics />} />
                  <Route path="/admin/crawler-analytics" element={<AdminCrawlerAnalytics />} />
                  <Route path="/admin/error-log" element={<AdminErrorLog />} />
                  <Route path="/admin/documents" element={<AdminDocuments />} />
                  <Route path="/admin/feedback" element={<AdminFeedback />} />
                  <Route path="/admin/home-settings" element={<AdminHomeSettings />} />
                  <Route path="/admin/performance" element={<AdminPerformanceScorecard />} />
                  <Route path="/admin/backlog" element={<AdminBacklog />} />
                  <Route path="/admin/certificates" element={<AdminCertificates />} />
                  <Route path="/admin/endless-quiz" element={<AdminEndlessQuiz />} />
                  <Route path="/admin/ads" element={<AdminAdsSettings />} />
                  <Route path="/admin/mailbox" element={<AdminMailbox />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/certificate/:id" element={<CertificateView />} />
                  <Route path="/badge/:id" element={<SharedBadge />} />
                  <Route path="/anup" element={<CreatorProfile />} />
                  <Route path="/products" element={<Products />} />
                  
                  <Route path="/concepts/llm-introduction" element={<ConceptLLMIntroduction />} />
                  <Route path="/concepts/rag-explained" element={<ConceptRAG />} />
                  <Route path="/concepts/mcp-explained" element={<ConceptMCP />} />
                  <Route path="/concepts/agents-explained" element={<ConceptAgents />} />
                  <Route path="/concepts/knowledge-graphs" element={<ConceptKnowledgeGraph />} />
                  <Route path="/concepts/vector-databases" element={<ConceptVectorDatabases />} />
                  <Route path="/knowledge-tree" element={<KnowledgeTree />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </HelmetProvider>
        </TooltipProvider>
      </FeedbackProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
